import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { RouteConfig } from '@/shared/lib/ts-router';

export type RouteWithoutChildren = Omit<RouteConfig, 'children' | 'element'> & {
  parent?: RouteWithoutChildren;
};

interface UseCurrentRouteConfigOptions {
  /**
   * Deprecated: children are not included in the returned route.
   * The hook returns a RouteWithoutChildren with a recursive `parent` chain.
   * @default false
   */
  includeChildren?: boolean;
}

/**
 * Flattens a nested routes structure into a record where keys are full paths
 * and values contain the route (without children) and a reference to its parent path.
 */
function flattenRoutes(routes: readonly RouteConfig[]): Record<string, RouteWithoutChildren> {
  const result: Record<string, RouteWithoutChildren> = {};

  function processRoute(route: RouteConfig, parentPath: string = '', parent?: RouteWithoutChildren) {
    const { children: _children, element: _element, ...routeWithoutChildren } = route;

    if (route.children?.length) {
      // Do not register group as a route, but propagate the current parent
      return route.children.forEach((r) => processRoute(r, route.path, routeWithoutChildren));
    }

    if (route.path) {
      const fullPath = `/${parentPath}/${route.path}`.replace(/\/+/g, '/');

      // Register this route with a reference to its nearest non-group parent
      result[fullPath] = {
        ...routeWithoutChildren,
        parent,
      };
    }
  }

  routes.forEach((route) => processRoute(route, '/', undefined));
  return result;
}

/**
 * Custom hook that returns the current RouteConfig for the active route
 *
 * This hook takes an array of RouteConfig objects and returns the most specific
 * RouteConfig that matches the current URL path. It uses react-router-dom's matchPath
 * function to determine matches and selects the most specific match based on path length.
 *
 * Features:
 * - Returns the most specific matching route when multiple routes match
 * - Handles nested routes correctly
 * - Option to include children in the returned route
 * - Returns undefined if no match is found
 *
 * @param routes - Array of RouteConfig objects to match against
 * @param options - Configuration options
 * @returns The matching RouteConfig object or undefined if no match is found
 */
export function useCurrentRouteConfig(routes: readonly RouteConfig[], options: UseCurrentRouteConfigOptions = {}) {
  const { includeChildren = false } = options;
  const location = useLocation();

  return useMemo(() => {
    const flattenedRoutes = flattenRoutes(routes);

    // Find all matching routes
    const matchingEntries = Object.entries(flattenedRoutes)
      .filter(([route]) => matchPath(route, location.pathname))
      .map(([route, config]) => ({
        route,
        config,
        // Calculate match specificity (longer paths are more specific)
        specificity: route.split('/').filter(Boolean).length,
      }));

    // Sort by specificity (descending) to get the most specific match first
    matchingEntries.sort((a, b) => b.specificity - a.specificity);

    // Return the most specific match, or undefined if no matches
    return matchingEntries[0]?.config;
  }, [routes, location.pathname, includeChildren]);
}
